import { DotsThree, Link as LinkIcon, PaperPlaneTilt, Prohibit, UserPlus } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  authInfoQuery,
  type Invite,
  type InviteStatus,
  invitesQuery,
  type LinkDelivery,
  rolesQuery,
  spacesQuery,
} from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { InviteDialog } from "@/components/admin/InviteDialog";
import { LinkDialog, type LinkResult } from "@/components/admin/LinkDialog";
import { plural, QueryBoundary, roleName, scopeText } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  type BadgeTone,
  Button,
  ConfirmDialog,
  type DataColumn,
  DataTable,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  MenuTrigger,
  RelativeTime,
  Select,
  TableSkeleton,
} from "@/components/ui";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/admin/invites")({ component: InvitesPage });

type Filter = "pending" | "all" | InviteStatus;

const statusTone: Record<InviteStatus, BadgeTone> = {
  pending: "accent",
  accepted: "success",
  expired: "warning",
  revoked: "neutral",
};
const statusLabel = (s: InviteStatus) =>
  s === "pending"
    ? m.adm_status_pending()
    : s === "accepted"
      ? m.adm_status_accepted()
      : s === "expired"
        ? m.adm_status_expired()
        : m.adm_status_revoked();

function InvitesPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_invites() }]);
  const q = useQuery(invitesQuery);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<LinkResult | null>(null);

  return (
    <Page
      title={m.adm_nav_invites()}
      icon={UserPlus}
      meta={m.adm_invites_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <Button variant="primary" onPress={() => setInviting(true)}>
          <UserPlus size={14} aria-hidden="true" />
          {m.adm_invite_title()}
        </Button>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={4} columns={4} />} needs="users.manage">
        {(invites) => <InviteTable invites={invites} onInvite={() => setInviting(true)} onLink={setLink} />}
      </QueryBoundary>
      <InviteDialog
        isOpen={inviting}
        onOpenChange={setInviting}
        onCreated={(res) => {
          if (res.link) {
            setLink({
              title: m.adm_invite_link_title(),
              intro: m.adm_invite_link_intro({ email: res.invite.email }),
              link: res.link,
              description: m.adm_invite_link_hint(),
            });
          } else {
            toast.show({ title: m.adm_invite_sent({ email: res.invite.email }), tone: "success" });
          }
        }}
      />
      <LinkDialog result={link} onClose={() => setLink(null)} />
    </Page>
  );
}

function InviteTable({
  invites,
  onInvite,
  onLink,
}: {
  invites: Invite[];
  onInvite: () => void;
  onLink: (r: LinkResult) => void;
}) {
  const qc = useQueryClient();
  const roles = useQuery(rolesQuery);
  const spaces = useQuery(spacesQuery);
  const auth = useQuery({ ...authInfoQuery, retry: false });
  const smtp = auth.data?.mail.smtpConfigured;
  const [filter, setFilter] = useState<Filter>("pending");
  const [revoking, setRevoking] = useState<Invite | null>(null);

  const roleById = useMemo(() => new Map((roles.data ?? []).map((r) => [r.id, r])), [roles.data]);
  const scopeNames = useMemo(() => {
    const out = new Map<string, string>();
    for (const s of spaces.data ?? []) {
      out.set(s.id, s.name);
      for (const p of s.products) out.set(p.id, p.name);
    }
    return out;
  }, [spaces.data]);

  const resend = useMutation({
    mutationFn: (i: Invite) => api.post<{ invite: Invite } & LinkDelivery>(`/api/v1/admin/invites/${i.id}/resend`),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: invitesQuery.queryKey });
      if (res.link) {
        onLink({
          title: m.adm_invite_link_title(),
          intro: m.adm_invite_link_intro({ email: res.invite.email }),
          link: res.link,
          description: m.adm_invite_link_hint(),
        });
      } else {
        toast.show({ title: m.adm_invite_sent({ email: res.invite.email }), tone: "success" });
      }
    },
    onError: (e) => toast.show({ title: adminError(e), tone: "danger" }),
  });

  const rows = invites.filter((i) => filter === "all" || i.status === filter);

  const columns: DataColumn<Invite>[] = [
    {
      id: "email",
      header: m.adm_invites_col_invitee(),
      isRowHeader: true,
      className: "min-w-52",
      cell: (i) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate-title font-medium text-fg" title={i.email}>
            {i.email}
          </span>
          {i.name ? <span className="truncate-title text-xs text-fg-muted">{i.name}</span> : null}
        </div>
      ),
    },
    {
      id: "role",
      header: m.adm_invites_col_access(),
      className: "min-w-44",
      cell: (i) => {
        const role = i.roleId ? roleById.get(i.roleId) : undefined;
        return (
          <div className="flex min-w-0 flex-col">
            <span className="truncate-title text-fg">
              {i.roleId ? (role ? roleName(role) : (i.roleSlug ?? m.adm_scope_unknown())) : m.adm_no_role()}
            </span>
            <span className="truncate-title text-xs text-fg-muted">
              {[
                i.roleId ? scopeText(i.scope, i.scopeId ? (scopeNames.get(i.scopeId) ?? null) : null) : null,
                i.groupIds.length > 0
                  ? plural(i.groupIds.length, m.adm_invites_groups_one, m.adm_invites_groups)
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        );
      },
    },
    {
      id: "status",
      header: m.adm_users_filter_status(),
      className: "w-28",
      cell: (i) => <Badge tone={statusTone[i.status]}>{statusLabel(i.status)}</Badge>,
    },
    {
      id: "sent",
      header: m.adm_invites_col_sent(),
      className: "hidden w-40 md:table-cell",
      cell: (i) => (
        <div className="flex min-w-0 flex-col">
          <RelativeTime value={i.createdAt} className="text-fg-muted" />
          {i.invitedByName ? (
            <span className="truncate-title text-xs text-fg-subtle">{m.adm_invites_by({ name: i.invitedByName })}</span>
          ) : null}
        </div>
      ),
    },
    {
      id: "expires",
      header: m.adm_invites_col_expires(),
      className: "hidden w-32 lg:table-cell",
      cell: (i) =>
        i.status === "pending" ? (
          <RelativeTime value={i.expiresAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">-</span>
        ),
    },
    {
      id: "actions",
      header: m.ui_actions(),
      hideHeader: true,
      align: "end",
      className: "w-12",
      cell: (i) =>
        i.status === "pending" || i.status === "expired" ? (
          <MenuTrigger>
            <IconButton
              aria-label={`${m.common_more()}: ${i.email}`}
              icon={DotsThree}
              iconWeight="bold"
              size="sm"
              noTooltip
            />
            <Menu placement="bottom end" aria-label={i.email}>
              <MenuItem id="resend" icon={smtp === false ? LinkIcon : PaperPlaneTilt} onAction={() => resend.mutate(i)}>
                {smtp === false ? m.adm_invites_new_link() : m.adm_invites_resend()}
              </MenuItem>
              {i.status === "pending" ? (
                <MenuItem id="revoke" icon={Prohibit} tone="danger" onAction={() => setRevoking(i)}>
                  {m.adm_invites_revoke()}
                </MenuItem>
              ) : null}
            </Menu>
          </MenuTrigger>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          label={m.adm_users_filter_status()}
          labelHidden
          className="w-44"
          items={[
            { id: "pending", label: m.adm_status_pending() },
            { id: "all", label: m.adm_users_status_all() },
            { id: "accepted", label: m.adm_status_accepted() },
            { id: "expired", label: m.adm_status_expired() },
            { id: "revoked", label: m.adm_status_revoked() },
          ]}
          selectedKey={filter}
          onSelectionChange={(k) => setFilter(k as Filter)}
        />
        <span className="ml-auto text-xs text-fg-muted tnum" aria-live="polite">
          {m.adm_users_count({ count: rows.length })}
        </span>
      </div>
      <DataTable
        label={m.adm_nav_invites()}
        columns={columns}
        rows={rows}
        renderEmptyState={() =>
          invites.length === 0 ? (
            <EmptyState
              title={m.adm_invites_empty_title()}
              description={m.adm_invites_empty_body()}
              action={
                <Button variant="secondary" onPress={onInvite}>
                  {m.adm_invite_title()}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={m.adm_invites_none_title()}
              description={m.adm_invites_none_body()}
              action={
                <Button variant="secondary" onPress={() => setFilter("all")}>
                  {m.filter_clear()}
                </Button>
              }
            />
          )
        }
      />
      <ConfirmDialog
        isOpen={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={m.adm_invites_revoke_title()}
        description={m.adm_invites_revoke_body({ email: revoking?.email ?? "" })}
        confirmLabel={m.adm_invites_revoke()}
        onConfirm={async () => {
          if (!revoking) return;
          await api.delete(`/api/v1/admin/invites/${revoking.id}`);
          await qc.invalidateQueries({ queryKey: invitesQuery.queryKey });
          toast.show({ title: m.adm_invites_revoked(), tone: "success" });
        }}
        describeError={adminError}
      />
    </div>
  );
}
